import React, { useState } from 'react';

import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { Skeleton } from 'antd';
import { useNavigate } from 'react-router-dom';
import NotFoundIllustration from '../../assets/undraw_not_found.svg';
import SearchIllustration from '../../assets/undraw_searching.svg';
import Button from '../../components/Button';
import InputSearch from '../../components/InputSearch';
import { Feedback } from '../../helpers/message';
import { useCustomTranslation } from '../../hooks/custom-translation';
import { Body as BodyText, H1, H3, H5 } from '../../styles/fonts';

import Enrichment from '../../components/ProfileTab/Enrichment';
import General from '../../components/ProfileTab/General';
import { Container, PageHeader } from '../../styles/contents';
import {
  Body,
  Column01,
  Column011,
  Column02,
  ContactHeader,
  ContactInfo,
  Data,
  GaugeContainer,
  SearchContainer,
  TabGroup,
  TabGroupPane,
  Text1,
} from './styles';

import ProfileAvatar from '../../components/Contacts/Contact/ContactProfile/ProfileAvatar';
import ProfileGaugeChart from '../../components/Contacts/Contact/ContactProfile/ProfileGaugeChart';
import { maskCpf, maskPhone } from '../../helpers/masks/masksRegex';
import * as CONSTANTS from '../../navigation/constants';
import { useSearchContactProfileQuery } from '../../services/contact-search';

const ContactProfileSearch = () => {
  const { tContactSegmentsEdit } = useCustomTranslation('contactSegmentsEdit');
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [maskedValue, setMaskedValue] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isValidCPF, setIsValidCPF] = useState(false);

  const testeCpf = (cpf: string) => {
    const cpfClean = cpf.replace(/\D/g, '');
    if (cpfClean.length !== 11 || /^(\d)\1{10}$/.test(cpfClean)) return false;
    let result = true;
    [9, 10].forEach(function (j) {
      let soma = 0;
      let r;
      cpfClean
        .split(/(?=)/)
        .splice(0, j)
        .forEach(function (e: any, i: any) {
          soma += parseInt(e, 10) * (j + 2 - (i + 1));
        });
      r = soma % 11;
      r = r < 2 ? 0 : 11 - r;
      if (r !== parseInt(cpfClean.substring(j, j + 1), 10)) result = false;
    });
    return result;
  };

  const { data: contacts, isFetching } = useSearchContactProfileQuery(
    {
      CPF: search,
    },
    {
      skip: !hasSearched || !search,
    },
  );

  const handleContactList = () => {
    navigate(CONSTANTS.DASHBOARD);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      const maskedCpfValue = maskCpf(value);
      setMaskedValue(maskedCpfValue === '-' ? '' : maskedCpfValue);
      const isValid = value.length === 11 ? testeCpf(value) : false;
      setIsValidCPF(isValid);
    }
  };

  const handleClear = () => {
    setHasSearched(false);
    setIsValidCPF(false);
    setSearch('');
    setMaskedValue('');
  };

  const handleSearch = () => {
    if (isValidCPF) {
      const cleanCPF = maskedValue.replace(/\D/g, '');
      setSearch(cleanCPF);
      setHasSearched(true);
    } else {
      Feedback.error('CPF inválido');
    }
  };

  let contactToShow;
  if (hasSearched && contacts?.data && contacts.data.length > 0) {
    [contactToShow] = contacts.data;
  } else {
    contactToShow = undefined;
  }

  const contact = contactToShow;

  const getContactEmail = () => {
    if (!contact) return '-';

    if (contact.enriched?.emailsJson) {
      try {
        const emails = JSON.parse(contact.enriched.emailsJson);
        if (emails && emails.length > 0) {
          return emails[0];
        }
      } catch {
        // Error parsing JSON
      }
    }

    if (contact.email) return contact.email;

    return (contact.enriched?.emails && contact.enriched.emails[0]) || '-';
  };

  const getContactPhone = () => {
    if (!contact) return '-';

    let phone = contact?.phoneNumber || '';

    if (!phone && contact.enriched) {
      try {
        if (contact.enriched.phoneNumbersJson) {
          const [firstPhone] = JSON.parse(contact.enriched.phoneNumbersJson);
          if (firstPhone) {
            phone = firstPhone;
          }
        }

        if (!phone && contact.enriched.cellPhonesJson) {
          const [firstCellPhone] = JSON.parse(contact.enriched.cellPhonesJson);
          if (firstCellPhone) {
            phone = firstCellPhone;
          }
        }
      } catch {
        // Error parsing JSON
      }
    }

    if (!phone && contact.enriched?.phoneNumbers) {
      const phoneStr = String(contact.enriched.phoneNumbers);
      const [firstPhone] = phoneStr.split(' ');
      if (firstPhone) {
        phone = firstPhone;
      }
    }

    return phone ? maskPhone(phone) : '-';
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const renderEmptyState = (illustration: string, message: string) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '48px',
      }}
    >
      <img
        src={illustration}
        alt="Ilustração"
        style={{
          width: '300px',
          marginBottom: '24px',
        }}
      />
      <H5 weight="regular" style={{ color: '#8C8C8C' }}>
        {message}
      </H5>
    </div>
  );

  return (
    <Container>
      <PageHeader>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleContactList}
        />
        <H3 weight="medium">
          {tContactSegmentsEdit('search_contact_profile')}
        </H3>
      </PageHeader>
      <Body>
        <SearchContainer>
          <InputSearch
            placeholder={tContactSegmentsEdit('search_by_document')}
            size="large"
            value={maskedValue}
            onChange={handleInputChange}
            onSearch={handleSearch}
            loading={isFetching}
            enterButton={<SearchOutlined />}
            status={hasSearched && !isValidCPF ? 'error' : undefined}
            allowClear
            onClear={handleClear}
            onKeyPress={handleKeyPress}
          />
        </SearchContainer>

        {(() => {
          if (!hasSearched) {
            return renderEmptyState(
              SearchIllustration,
              tContactSegmentsEdit('search_to_find_contacts'),
            );
          }

          if (isFetching) {
            return (
              <>
                <ContactHeader>
                  <Column01>
                    <ContactInfo>
                      <Skeleton.Avatar
                        active
                        size={64}
                        style={{ marginRight: '16px' }}
                      />
                      <Data>
                        <Skeleton.Input
                          active
                          size="large"
                          style={{ width: '200px', marginBottom: '8px' }}
                        />
                        <Skeleton.Input
                          active
                          style={{ width: '160px', marginBottom: '4px' }}
                        />
                        <Skeleton.Input active style={{ width: '140px' }} />
                      </Data>
                    </ContactInfo>
                  </Column01>
                  <Column02>
                    <Column011>
                      <GaugeContainer>
                        <Skeleton.Avatar active size={120} shape="circle" />
                      </GaugeContainer>
                      <Text1>
                        <Skeleton.Input
                          active
                          size="large"
                          style={{ width: '60px', marginBottom: '8px' }}
                        />
                        <Skeleton.Input active style={{ width: '120px' }} />
                      </Text1>
                    </Column011>
                  </Column02>
                </ContactHeader>
                <TabGroup tabPosition="top" type="line" size="middle">
                  <TabGroupPane tab={tContactSegmentsEdit('overview')} key="0">
                    <div style={{ padding: '24px' }}>
                      <Skeleton active paragraph={{ rows: 4 }} />
                      <div style={{ marginTop: '24px' }}>
                        <Skeleton.Input
                          active
                          size="large"
                          style={{ width: '100%', marginBottom: '16px' }}
                        />
                        <Skeleton active paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  </TabGroupPane>
                  <TabGroupPane
                    tab={tContactSegmentsEdit('enrichment')}
                    key="1"
                  >
                    <div style={{ padding: '24px' }}>
                      <Skeleton active paragraph={{ rows: 4 }} />
                      <div style={{ marginTop: '24px' }}>
                        <Skeleton.Input
                          active
                          size="large"
                          style={{ width: '100%', marginBottom: '16px' }}
                        />
                        <Skeleton active paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  </TabGroupPane>
                </TabGroup>
              </>
            );
          }

          if (!contacts?.data?.length) {
            return renderEmptyState(
              NotFoundIllustration,
              tContactSegmentsEdit('no_contact_found'),
            );
          }

          return (
            <>
              <ContactHeader>
                <Column01>
                  <ContactInfo>
                    <ProfileAvatar gender={contact?.enriched?.gender || 'I'} />
                    <Data>
                      <H3 weight="medium">{contact?.enriched?.name || '-'}</H3>
                      <H5 weight="regular">{getContactEmail()}</H5>
                      <H5 weight="regular">{getContactPhone()}</H5>
                    </Data>
                  </ContactInfo>
                </Column01>
                <Column02>
                  <Column011>
                    <GaugeContainer>
                      <ProfileGaugeChart
                        value={contact?.enriched?.financialScoreInt || 0}
                      />
                    </GaugeContainer>
                    <Text1>
                      <H1 weight="medium">
                        {contact?.enriched?.financialScoreInt || '-'}
                      </H1>
                      <BodyText weight="medium">
                        {tContactSegmentsEdit('psychometric_score')}
                      </BodyText>
                    </Text1>
                  </Column011>
                </Column02>
              </ContactHeader>
              <TabGroup tabPosition="top" type="line" size="middle">
                <TabGroupPane tab={tContactSegmentsEdit('overview')} key="0">
                  <General contactInfo={contact} />
                </TabGroupPane>
                <TabGroupPane tab={tContactSegmentsEdit('enrichment')} key="1">
                  <Enrichment contactInfo={contact} />
                </TabGroupPane>
              </TabGroup>
            </>
          );
        })()}
      </Body>
    </Container>
  );
};

export default ContactProfileSearch;
